"use client";

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Card } from "@/components/ui-system";
import {
  getWorkspaceEntitlementPolicy,
  updateWorkspaceEntitlementPolicy,
  type WorkspaceEntitlementPolicyDto,
} from "@/lib/api/endpoints/entitlements";
import { hasModulePermission, useEffectivePermissions } from "@/modules/auth/access";
import { useAuthStore } from "@/modules/auth/store";

type PolicyDraft = Pick<
  WorkspaceEntitlementPolicyDto,
  | "default_warranty_months"
  | "post_expiry_read_days"
  | "ingest_after_expiry"
  | "block_explorer_after_expiry"
  | "block_analytics_after_expiry"
  | "block_live_after_expiry"
  | "stop_reports_after_expiry"
  | "stop_alerts_after_expiry"
  | "allow_device_override"
>;

function toDraft(policy: WorkspaceEntitlementPolicyDto): PolicyDraft {
  return {
    default_warranty_months: policy.default_warranty_months,
    post_expiry_read_days: policy.post_expiry_read_days,
    ingest_after_expiry: policy.ingest_after_expiry,
    block_explorer_after_expiry: policy.block_explorer_after_expiry,
    block_analytics_after_expiry: policy.block_analytics_after_expiry,
    block_live_after_expiry: policy.block_live_after_expiry,
    stop_reports_after_expiry: policy.stop_reports_after_expiry,
    stop_alerts_after_expiry: policy.stop_alerts_after_expiry,
    allow_device_override: policy.allow_device_override,
  };
}

export function EntitlementsPage() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const tenantId = useAuthStore((s) => s.tenantId);
  const { permissionMap } = useEffectivePermissions();
  const isSuperAdmin = user?.role === "super_admin";
  const isPlatformActor = isSuperAdmin || user?.accountType === "platform";
  const canEdit = hasModulePermission(user, permissionMap, "entitlements", "edit");
  const [draft, setDraft] = useState<PolicyDraft | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const effectiveTenantId = tenantId;
  const requiresWorkspaceSelection = isPlatformActor && !effectiveTenantId;

  const query = useQuery({
    queryKey: ["entitlements", "workspace", effectiveTenantId ?? "active"],
    queryFn: () => getWorkspaceEntitlementPolicy(isSuperAdmin ? effectiveTenantId : null),
    enabled: Boolean(effectiveTenantId) && !requiresWorkspaceSelection,
    staleTime: 30_000,
  });

  useEffect(() => {
    setDraft(null);
    setMessage(null);
  }, [effectiveTenantId]);

  useEffect(() => {
    if (query.data) {
      setDraft(toDraft(query.data));
    }
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: (body: PolicyDraft) => updateWorkspaceEntitlementPolicy(body, isSuperAdmin ? effectiveTenantId : null),
    onSuccess: async (policy) => {
      setDraft(toDraft(policy));
      setMessage("Entitlement policy saved.");
      await queryClient.invalidateQueries({ queryKey: ["entitlements", "workspace"] });
    },
    onError: (error) => {
      setMessage(error instanceof Error ? error.message : "Could not save entitlement policy.");
    },
  });

  const dirty = useMemo(() => {
    if (!draft || !query.data) return false;
    return JSON.stringify(draft) !== JSON.stringify(toDraft(query.data));
  }, [draft, query.data]);

  function patch(next: Partial<PolicyDraft>) {
    setDraft((current) => (current ? { ...current, ...next } : current));
    setMessage(null);
  }

  return (
    <>
      <div className="mx-auto w-full max-w-workspace px-ds-3 pb-ds-8 pt-ds-3 sm:px-ds-4 md:pt-ds-4 lg:px-ds-5">
        {query.isError ? (
          <Card className="border-error/25 bg-error/5 p-ds-4 text-error">
            {query.error instanceof Error ? query.error.message : "Could not load entitlement policy."}
          </Card>
        ) : null}

        <Card className="space-y-ds-5 p-ds-5">
          <div className="flex flex-wrap items-start justify-between gap-ds-3">
            <div>
              <p className="text-title font-semibold text-foreground">Workspace policy</p>
              <p className="mt-ds-1 text-caption text-muted">
                Applies to all devices in the selected workspace. Ingestion remains open.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-ds-2">
              {message ? <span className="text-caption text-muted">{message}</span> : null}
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={!dirty || mutation.isPending || !draft}
                onClick={() => query.data && setDraft(toDraft(query.data))}
              >
                Reset
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={!canEdit || !dirty || mutation.isPending || !draft}
                onClick={() => draft && mutation.mutate(draft)}
              >
                {mutation.isPending ? "Saving..." : "Save policy"}
              </button>
            </div>
          </div>

          {requiresWorkspaceSelection ? (
            <div className="rounded-card border border-border bg-surface-secondary px-ds-4 py-ds-3 text-caption text-muted">
              Select one workspace in the app header to view or edit its entitlement policy.
            </div>
          ) : null}

          {!canEdit ? (
            <div className="rounded-card border border-card-border bg-surface-secondary px-ds-4 py-ds-3 text-caption text-muted">
              You can view this policy, but editing requires entitlements edit permission.
            </div>
          ) : null}

          {!draft ? (
            <div className="rounded-card border border-card-border bg-surface-secondary px-ds-4 py-ds-6 text-caption text-muted">
              Loading workspace policy...
            </div>
          ) : (
            <div className="grid gap-ds-4 lg:grid-cols-2">
              <NumberField
                label="Default warranty"
                suffix="months"
                value={draft.default_warranty_months}
                disabled={!canEdit}
                min={1}
                max={120}
                onChange={(value) => patch({ default_warranty_months: value })}
              />
              <NumberField
                label="Post-expiry read window"
                suffix="days"
                value={draft.post_expiry_read_days}
                disabled={!canEdit}
                min={0}
                max={3650}
                onChange={(value) => patch({ post_expiry_read_days: value })}
              />
              <ToggleField
                label="Explorer blocked after read window"
                checked={draft.block_explorer_after_expiry}
                disabled={!canEdit}
                onChange={(value) => patch({ block_explorer_after_expiry: value })}
              />
              <ToggleField
                label="Analytics blocked after read window"
                checked={draft.block_analytics_after_expiry}
                disabled={!canEdit}
                onChange={(value) => patch({ block_analytics_after_expiry: value })}
              />
              <ToggleField
                label="Live blocked after read window"
                checked={draft.block_live_after_expiry}
                disabled={!canEdit}
                onChange={(value) => patch({ block_live_after_expiry: value })}
              />
              <ToggleField
                label="Stop reports after warranty expiry"
                checked={draft.stop_reports_after_expiry}
                disabled={!canEdit}
                onChange={(value) => patch({ stop_reports_after_expiry: value })}
              />
              <ToggleField
                label="Stop alerts after warranty expiry"
                checked={draft.stop_alerts_after_expiry}
                disabled={!canEdit}
                onChange={(value) => patch({ stop_alerts_after_expiry: value })}
              />
              <ToggleField
                label="Allow ingestion after expiry"
                checked={draft.ingest_after_expiry}
                disabled
                onChange={(value) => patch({ ingest_after_expiry: value })}
              />
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

function NumberField({
  label,
  suffix,
  value,
  disabled,
  min,
  max,
  onChange,
}: {
  label: string;
  suffix: string;
  value: number;
  disabled: boolean;
  min: number;
  max: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="rounded-card border border-card-border bg-surface-primary p-ds-4">
      <span className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">{label}</span>
      <div className="mt-ds-2 flex items-center gap-ds-2">
        <input
          className="ui-control max-w-[9rem]"
          type="number"
          min={min}
          max={max}
          disabled={disabled}
          value={value}
          onChange={(event) => {
            const next = Number(event.target.value);
            onChange(Number.isFinite(next) ? Math.min(max, Math.max(min, next)) : min);
          }}
        />
        <span className="text-caption text-muted">{suffix}</span>
      </div>
    </label>
  );
}

function ToggleField({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-ds-3 rounded-card border border-card-border bg-surface-primary p-ds-4">
      <span className="text-caption font-medium text-foreground">{label}</span>
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-border text-primary focus:ring-primary/20"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
    </label>
  );
}
